import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { closable: { type: Boolean, default: true } }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)

    // Listen for Turbo Frame load - open if content, close if empty
    this.element.addEventListener('turbo:frame-load', this.handleFrameLoad.bind(this))

    // Use MutationObserver to detect when content is cleared (by Turbo Stream)
    this.observer = new MutationObserver(this.handleMutation.bind(this))
    this.observer.observe(this.element, { childList: true, subtree: true })

    // If content already exists, open the modal
    if (this.element.innerHTML.trim() !== '') {
      this.open()
    }
  }

  handleFrameLoad() {
    // Check if the frame loaded with content or empty
    if (this.element.innerHTML.trim() !== '') {
      this.open()
    } else {
      // Frame loaded empty (e.g., Cancel clicked, navigated to page without matching frame)
      this.element.classList.add('hidden')
      document.body.classList.remove('overflow-hidden')
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
    document.body.classList.remove('overflow-hidden')
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  // Handle DOM mutations - close modal if content becomes empty
  handleMutation() {
    // Check if content was cleared while modal is visible
    if (!this.element.classList.contains('hidden') && this.element.innerHTML.trim() === '') {
      this.element.classList.add('hidden')
      document.body.classList.remove('overflow-hidden')
    }
  }

  open() {
    this.element.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    this.focusFirstElement()
  }

  close() {
    if (!this.closableValue) return

    this.element.classList.add('hidden')
    this.element.innerHTML = ''
    document.body.classList.remove('overflow-hidden')
  }

  // Called from backdrop click
  closeBackground(event) {
    // Only close if clicking the backdrop itself, not modal content
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  handleKeydown(event) {
    // Only handle Escape if modal is visible
    if (event.key === 'Escape' && this.closableValue && !this.element.classList.contains('hidden')) {
      this.close()
    }
  }

  focusFirstElement() {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      const focusable = this.element.querySelectorAll(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length > 0) {
        focusable[0].focus()
      }
    }, 100)
  }
}
